import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X, Baby, Plus, Trash2, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLimits } from '@/hooks/useLimits';
import { useTranslation } from '@parentpath/i18n';

export type BabyNameEntry = {
  name: string;
  gender: 'male' | 'female' | 'neutral';
};

interface BabyNamesModalProps {
  isOpen: boolean;
  onClose: () => void;
  babyNames: BabyNameEntry[];
  childCount: number;
  onSave: (names: BabyNameEntry[]) => Promise<void>;
}

export function BabyNamesModal({
  isOpen,
  onClose,
  babyNames,
  childCount,
  onSave,
}: BabyNamesModalProps) {
  const { t } = useTranslation(['profile', 'common']);
  const { getNumberLimit } = useLimits();

  const genderOptions: { value: BabyNameEntry['gender']; label: string; activeColor: string }[] = [
    { value: 'male' as const, label: t('profile:modals.babyNames.genderBoy'), activeColor: 'bg-pp-blue-boy text-white' },
    { value: 'female' as const, label: t('profile:modals.babyNames.genderGirl'), activeColor: 'bg-pp-pink-girl text-white' },
    { value: 'neutral' as const, label: t('profile:modals.babyNames.genderNeutral'), activeColor: 'bg-white/30 text-white' },
  ];
  const [entries, setEntries] = useState<BabyNameEntry[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Get limit for baby names (0 = disabled, 1-5 = max names)
  const maxNames = getNumberLimit('baby_names_assign');
  const isFeatureEnabled = maxNames > 0;
  const canAddMore = entries.length < maxNames;

  // Initialize entries when modal opens
  useEffect(() => {
    if (isOpen) {
      // Start with existing names or create initial empty slots based on childCount
      const initial: BabyNameEntry[] = babyNames.length > 0
        ? [...babyNames]
        : Array.from({ length: childCount }, () => ({ name: '', gender: 'neutral' as const }));
      // Respect the limit when initializing
      setEntries(maxNames > 0 ? initial.slice(0, maxNames) : initial);
    }
  }, [isOpen, babyNames, childCount, maxNames]);

  if (!isOpen) return null;

  // Show premium upgrade if feature is disabled
  if (!isFeatureEnabled) {
    const premiumContent = (
      <>
        <div className="bottom-sheet-backdrop" onClick={onClose} />
        <div className="bottom-sheet-container">
          <div className="bottom-sheet-modal">
            <div className="bottom-sheet-header">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-pp-orange/20 rounded-full flex items-center justify-center">
                  <Baby className="w-5 h-5 text-pp-orange" />
                </div>
                <h2>{t('profile:modals.babyNames.title')}</h2>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bottom-sheet-content text-center py-6">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-pp-orange" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{t('profile:modals.babyNames.premiumTitle')}</h3>
              <p className="text-white/60">
                {t('profile:modals.babyNames.premiumDescription')}
              </p>
            </div>
            <div className="bottom-sheet-footer">
              <Link
                to="/subscription"
                onClick={onClose}
                className="w-full py-3 bg-pp-orange text-white font-medium rounded-xl text-center hover:bg-pp-orange/90 transition-colors"
              >
                {t('profile:modals.babyNames.premiumUpgrade')}
              </Link>
            </div>
          </div>
        </div>
      </>
    );
    return createPortal(premiumContent, document.body);
  }

  const handleNameChange = (index: number, value: string) => {
    const newEntries = [...entries];
    newEntries[index] = { ...newEntries[index], name: value };
    setEntries(newEntries);
  };

  const handleGenderChange = (index: number, gender: BabyNameEntry['gender']) => {
    const newEntries = [...entries];
    newEntries[index] = { ...newEntries[index], gender };
    setEntries(newEntries);
  };

  const handleAddName = () => {
    setEntries([...entries, { name: '', gender: 'neutral' }]);
  };

  const handleRemoveName = (index: number) => {
    if (entries.length <= 1) return;
    const newEntries = entries.filter((_, i) => i !== index);
    setEntries(newEntries);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Filter out entries with empty names
      const filteredEntries = entries.filter((entry) => entry.name.trim() !== '');
      await onSave(filteredEntries);
      onClose();
    } catch (err) {
      console.error('Error saving baby names:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Get placeholder text based on index
  const getPlaceholder = (index: number): string => {
    if (entries.length === 1) return t('profile:modals.babyNames.placeholderSingle');
    return t('profile:modals.babyNames.placeholderMultiple', { index: index + 1 });
  };

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pp-orange/20 rounded-full flex items-center justify-center">
                <Baby className="w-5 h-5 text-pp-orange" />
              </div>
              <h2>{t(`profile:modals.babyNames.titleByCount.${childCount}`) || t('profile:modals.babyNames.title')}</h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content - scrollable */}
          <div className="bottom-sheet-content space-y-4">
            <p className="text-sm text-white/70">
              {childCount === 1 ? t('profile:modals.babyNames.description') : t('profile:modals.babyNames.descriptionPlural', { count: childCount })}
            </p>

            {/* Name Entries */}
            <div className="space-y-4">
              {entries.map((entry, index) => (
                <div key={index} className="p-3 bg-white/10 rounded-[15px] space-y-3">
                  {/* Name Input Row */}
                  <div className="flex items-center gap-2">
                    <input
                      value={entry.name}
                      onChange={(e) => handleNameChange(index, e.target.value)}
                      placeholder={getPlaceholder(index)}
                      maxLength={50}
                      className="flex-1 px-4 py-2.5 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                    />
                    {entries.length > 1 && (
                      <button
                        onClick={() => handleRemoveName(index)}
                        className="p-2 hover:bg-red-500/20 rounded-[5px] transition-colors text-white/50 hover:text-red-400"
                        title={t('profile:modals.babyNames.remove')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Gender Selection */}
                  <div className="flex gap-2">
                    {genderOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleGenderChange(index, option.value)}
                        className={cn(
                          'flex-1 py-2 px-3 rounded-[10px] text-sm font-medium transition-all',
                          entry.gender === option.value
                            ? option.activeColor
                            : 'bg-white/10 text-white/60 hover:bg-white/15'
                        )}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Add Name Button */}
            {canAddMore ? (
              <button
                onClick={handleAddName}
                className="w-full py-3 border-2 border-dashed border-white/30 rounded-[15px] text-white/60 hover:border-pp-orange hover:text-pp-orange transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {t('profile:modals.babyNames.addMore')}
              </button>
            ) : (
              <p className="text-xs text-white/50 text-center">
                {t('profile:modals.babyNames.maxReached', { max: maxNames })}
              </p>
            )}

            {/* Hint */}
            <p className="text-xs text-white/50">
              {t('profile:modals.babyNames.hint')}
            </p>
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
            >
              {isSaving ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('common:save')
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
